import React, { useState, useEffect, useLayoutEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { infoTipsEnabled, onInfoTipsChange } from '../lib/infoTips';

// Teal ⓘ tooltip for the supplier portal (2026-06 info-icons pass 1).
// One reusable component for the ~20 placements across the dashboard,
// brief detail, response form, and decline surfaces.
//
// Visual:
//   16px circle, 1px teal outline (#2E8FA1), italic serif 'i' in teal.
//   Teal = info/view in our design system (per the recognition brief),
//   which is the same hue the supplier sees elsewhere for "view file"
//   chips, brief comment mentions in emails, etc.
//
// Behaviour:
//   - Hover (desktop) opens the popover; mouseleave closes it.
//   - Keyboard focus on the button opens it; blur closes.
//   - Touch tap toggles. Outside tap closes. Escape closes.
//   - Touch is detected via pointer events firing with pointerType
//     === 'touch' — we don't try to feature-detect mobile, just react
//     to the actual input.
//
// Accessibility:
//   - Renders a real <button> with an aria-label. The popover is
//     linked via aria-describedby so screen readers announce the
//     descriptive content when the button is focused.
//   - prefers-reduced-motion skips the open/close fade — popover
//     just shows/hides instantly.
//
// Placement:
//   - Inline, vertical-align middle, small left margin.
//   - Popover positions itself to the RIGHT of the icon by default;
//     when the icon sits near the right edge of the viewport,
//     popover right-aligns instead (auto-detected via getBoundingClientRect).

const POPOVER_WIDTH_DEFAULT = 250;
const POPOVER_WIDTH_WIDE = 290;

export default function InfoIcon({
  heading,
  children,
  width = POPOVER_WIDTH_DEFAULT,
  ariaLabel,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [arrowOffset, setArrowOffset] = useState(0);
  // Fixed-position coords (portaled to body so the popover escapes any
  // overflow-hidden / scroll ancestor) + a flip flag for when there isn't
  // room below the icon.
  const [pos, setPos] = useState(null);
  const [flipUp, setFlipUp] = useState(false);
  // Per-portal ⓘ on/off preference (toggled from the account menu).
  const [tipsOn, setTipsOn] = useState(infoTipsEnabled());
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = useId();

  // Dedicated portal host. We create and own a <div> appended to <body>
  // rather than portaling straight into document.body — passing document.body
  // to createPortal proved brittle (it could throw "Target container is not a
  // DOM element" and blank the whole page when hovering an ⓘ). Our own node is
  // guaranteed to be a valid element before createPortal ever sees it.
  const portalElRef = useRef(null);
  if (portalElRef.current === null && typeof document !== 'undefined') {
    portalElRef.current = document.createElement('div');
    portalElRef.current.setAttribute('data-info-popover-host', '');
  }
  useEffect(() => {
    const el = portalElRef.current;
    if (!el || typeof document === 'undefined' || !document.body) return undefined;
    document.body.appendChild(el);
    return () => { try { document.body.removeChild(el); } catch { /* already gone */ } };
  }, []);

  useEffect(() => onInfoTipsChange(() => setTipsOn(infoTipsEnabled())), []);

  // Outside tap + Escape close the popover.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inWrapper && !inPopover) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // The popover is fixed-positioned from the icon's rect, so any scroll
    // would leave it stranded — close it instead of repositioning.
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // When opening, decide whether to right-align the popover so it
  // doesn't clip at the viewport's right edge. Also compute the
  // arrow's horizontal offset so it points back at the icon
  // regardless of which side the popover ended up on.
  useEffect(() => {
    if (!open || !wrapperRef.current) { setPos(null); return; }
    const iconRect = wrapperRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    // Popover's left edge if left-aligned (same as icon left) extends to
    // iconRect.left + width. If that overflows the viewport by a margin,
    // swap to right-align.
    const wouldOverflowRight = iconRect.left + width > vw - 16;
    setAlignRight(wouldOverflowRight);
    setArrowOffset(12);
    setFlipUp(false);
    setPos({
      iconTop: iconRect.top,
      iconBottom: iconRect.bottom,
      left: wouldOverflowRight ? Math.max(8, iconRect.right - width) : Math.min(iconRect.left, vw - width - 8),
    });
  }, [open, width]);

  // After the popover renders, flip it above the icon when there isn't
  // room below it in the viewport (and there is room above).
  useLayoutEffect(() => {
    if (!open || !pos || !popoverRef.current) return;
    const h = popoverRef.current.offsetHeight;
    setFlipUp(pos.iconBottom + 8 + h > window.innerHeight - 8 && pos.iconTop - 8 - h > 8);
  }, [open, pos]);

  const handlePointerEnter = (e) => {
    // Mouse hover only. Touch tap is handled by onClick below.
    if (e.pointerType === 'touch') return;
    setOpen(true);
  };
  const handlePointerLeave = (e) => {
    if (e.pointerType === 'touch') return;
    setOpen(false);
  };
  const handleClick = () => setOpen(o => !o);
  const handleFocus = () => setOpen(true);
  const handleBlur = (e) => {
    // Don't close on blur if focus moved INTO the popover (e.g. the
    // user tabbed to interactive content inside). Otherwise close.
    if (popoverRef.current && popoverRef.current.contains(e.relatedTarget)) return;
    setOpen(false);
  };

  // Supplier toggled the ⓘ tips off — render nothing (the label it sits
  // beside stays; just the icon disappears).
  if (!tipsOn) return null;

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex align-middle ml-1.5 ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel || `Info: ${heading || ''}`}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="info-icon-btn inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#2E8FA1] text-[#2E8FA1] bg-transparent leading-none cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8FA1]/60 focus-visible:ring-offset-0">
        <span
          aria-hidden="true"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: '11px',
            lineHeight: 1,
            display: 'inline-block',
            marginTop: '-1px',
          }}>
          i
        </span>
      </button>
      {open && pos && portalElRef.current && createPortal(
        <span
          ref={popoverRef}
          id={popoverId}
          role="tooltip"
          className="info-icon-popover fixed z-[10000] pointer-events-none"
          style={{
            left: pos.left,
            width,
            ...(flipUp
              ? { bottom: window.innerHeight - pos.iconTop + 8 }
              : { top: pos.iconBottom + 8 }),
          }}>
          {/* Arrow — small triangle pointing at the icon (up when the
              popover is below, down when it's flipped above). */}
          <span
            aria-hidden="true"
            className="absolute"
            style={{
              [flipUp ? 'bottom' : 'top']: -6,
              [alignRight ? 'right' : 'left']: arrowOffset,
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              ...(flipUp ? { borderTop: '6px solid #ffffff' } : { borderBottom: '6px solid #ffffff' }),
            }} />
          <span
            className="pointer-events-auto block bg-white border border-[#D4D9C6] rounded-[10px] px-3.5 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.14)] normal-case tracking-normal">
            {/* normal-case + tracking-normal on the wrapper above reset the
                text-transform / letter-spacing the popover would otherwise
                INHERIT from an uppercase parent (e.g. the Your Briefs <h2>
                or a fact label) — without it the body copy renders in caps.
                The heading span re-applies its own uppercase + tracking. */}
            {heading && (
              <span className="block font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#2E8FA1] mb-1.5">
                {heading}
              </span>
            )}
            <span className="block font-['Metropolis'] text-[12.5px] text-fusion-mid-grey leading-relaxed normal-case tracking-normal">
              {children}
            </span>
          </span>
        </span>,
        portalElRef.current
      )}
    </span>
  );
}

InfoIcon.WIDE = POPOVER_WIDTH_WIDE;
InfoIcon.DEFAULT_WIDTH = POPOVER_WIDTH_DEFAULT;
